import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FileIcon } from 'lucide-react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configuration de PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PDFPreviewProps {
  fileUrl: string;
  width?: number;
  height?: number;
  className?: string;
}

const PDFPreview: React.FC<PDFPreviewProps> = ({
  fileUrl,
  width = 40,
  height = 40,
  className = ""
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfData, setPdfData] = useState<Blob | null>(null);

  useEffect(() => {
    if (!fileUrl || fileUrl.trim() === '') {
      setError(true);
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(false);
        setPdfData(null);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(fileUrl, {
          signal: controller.signal,
          mode: 'cors',
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        if (!isCancelled) {
          setPdfData(blob);
          setLoading(false);
        }
      } catch (err) {
        console.error('Erreur lors du chargement du PDF depuis S3:', err);
        if (!isCancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isCancelled = true;
    };
  }, [fileUrl]);

  const onDocumentLoadSuccess = useCallback(() => {
    setLoading(false);
    setError(false);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('Erreur lors du chargement du PDF:', error);

    // Si c'est une erreur 403 (Forbidden), c'est probablement un fichier S3 avec authentification
    // Dans ce cas, on affiche un indicateur spécial au lieu de l'icône générique
    if (error.message.includes('403') || error.message.includes('Forbidden')) {
      // On ne met pas error à true, on garde loading à false pour afficher l'aperçu spécial
      setLoading(false);
      return;
    }

    setError(true);
    setLoading(false);
  }, []);

  // Si erreur, afficher l'icône par défaut
  if (error) {
    return (
      <div
        className={`w-[${width}px] h-[${height}px] flex justify-center items-center bg-gray-100 rounded-md ${className}`}
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        <FileIcon className="w-4 h-4 text-gray-500" />
      </div>
    );
  }

  // Si en cours de chargement, afficher le spinner
  if (loading) {
    return (
      <div
        className={`w-[${width}px] h-[${height}px] flex justify-center items-center bg-gray-100 rounded-md ${className}`}
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Si pas de pages mais pas d'erreur, c'est probablement un fichier S3 avec authentification
  if (!pdfData && !error) {
    return (
      <div
        className={`w-[${width}px] h-[${height}px] flex justify-center items-center bg-blue-100 rounded-md border-2 border-blue-300 ${className}`}
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        <span className="text-xs font-bold text-blue-600">PDF</span>
      </div>
    );
  }


  return (
    <div
      className={`flex justify-center items-center bg-gray-100 rounded-md overflow-hidden cursor-pointer ${className}`}
      style={{ width: `${width}px`, height: `${height}px` }}
      onClick={() => {
        window.open(fileUrl, '_blank');
      }}
    >
      {pdfData && (
        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
        >
          <Page
            pageNumber={1}
            width={width}
            height={height}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="border-0"
          />
        </Document>
      )}
    </div>
  );
};

export default PDFPreview;
